import { TextAlignment, TextBackground, TextVerticalAlignment } from "building-types/annotation/annotationTypesEnum";
import type { LineStyle, TextStyle } from "building-types/views/Primitive2DOutput";
import colors from "common/constants/colorsOld";
import { ONE_POINT, ZERO_INCHES } from "common/constants/scaleFactors";
import { numMul, percentNew, sheetPointsNew, sheetPointsToInches } from "common/utils/nominalNumbers";
import type { Mutable } from "common/utils/types";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { HatchMaterialType } from "geom/hatchTypes";
import { fonts, STANDARD_FONT_SIZE } from "pdf/planFonts";
import { TEXT_STROKE_WIDTH } from "./constants";

export type StyleSheetBase = Record<string, object | undefined>;
export type StyleSheet<T extends StyleSheetBase> = { readonly [K in keyof T]: T[K] };

// internal module use only
type ParsedStyleSheet<T extends StyleSheetBase> = StyleSheet<{ readonly [K in keyof T]: T[K] }>;
type RequiredStyleSheet<T extends StyleSheetBase> = { readonly [K in keyof T]: Required<T[K]> };

export function styleSheetNew<T extends StyleSheetBase>(source: T): ParsedStyleSheet<T> {
  return { ...source };
}

/**
 * Override two styles
 * @param style original style
 * @param merge style overrides
 * @returns merged style
 */
export function styleMerge<T extends object>(style: T, merge: T): T {
  return { ...style, ...merge };
}

/**
 * Merge one style into another or create a new style on a sheet. The new style will have the original properties of the target and the new style.
 * Properties on existing styles will be overriden by new styles with similar properties
 * @param sheet target sheet to merge to
 * @param style style key
 * @param merge values to create or override a style
 * @returns a new sheet with all added styles
 */
export function styleSheetMergeIn<T extends StyleSheet<any>>(
  sheet: T,
  style: keyof T,
  merge: Partial<T[typeof style]>,
): T {
  return { ...sheet, [style]: styleMerge(sheet[style], merge) };
}

/**
 * Merges new styles onto a style sheet and merges existing styles into the source sheet
 * @param sheet sheet to merge to
 * @param styles styles to add to the sheet - these will be merged to styles if existing, or created if not
 * @returns a new sheet with all added styles
 */
export function styleSheetMerge<T extends StyleSheetBase, S extends StyleSheetBase>(
  sheet: T,
  styles: S | undefined,
): ParsedStyleSheet<T & S> {
  let copy = { ...sheet } as ParsedStyleSheet<T & S>;
  for (const style in styles) {
    const value = styles[style];
    if (value !== undefined && value !== null) {
      copy = styleSheetMergeIn(copy, style, value);
    }
  }
  return copy;
}

/**
 * Merges multiple style sheets into one
 * @param stylesheets sheets to merge
 * @returns a new sheet with the parameter sheets merged together
 */
export function styleSheetCascade<T extends StyleSheetBase>(...stylesheets: T[]) {
  let sheet = stylesheets[0];
  for (let i = 1; i < stylesheets.length; i++) {
    sheet = styleSheetMerge(sheet, stylesheets[i]);
  }
  return sheet;
}

// global lines
const line: Required<Omit<LineStyle, "hatchMaterial">> & Pick<LineStyle, "hatchMaterial"> = {
  linewidth: ONE_POINT,
  dash: false,
  dashMask: false,
  dashLength: [sheetPointsNew(6), sheetPointsNew(4)],
  fill: false,
  fillColor: colors.black,
  fillOpacity: percentNew(0.8),
  strokeColor: colors.black,
  strokeOpacity: percentNew(1.0),
  hatchMaterial: {
    type: HatchMaterialType.PROCEDURAL,
    materialId: HatchMaterialId.None,
  },
  hatchStroke: true,
  hatch: false,
  hatchLineWidth: sheetPointsNew(0.25),
  hatchStrokeColor: colors.black,
  // fallback for hatches
  hatchCrossHatch: false,
  hatchDirection: [1, 1],
  hatchLineSpacing: sheetPointsToInches(sheetPointsNew(6.0)),
};

// global text
const text: Required<TextStyle> = {
  fontSize: STANDARD_FONT_SIZE,
  fontColor: colors.black,
  font: fonts.planSetRegular,
  fontOpacity: percentNew(1),
  background: TextBackground.OUTLINE,
  backgroundColor: colors.white,
  strokeColor: colors.white,
  strokeWidth: TEXT_STROKE_WIDTH,
  alignment: TextAlignment.CENTER,
  verticalAlignment: TextVerticalAlignment.CENTER,
  boxVerticalAlignmentPercentageOffset: ZERO_INCHES,
};

/**
 * Global styles that will be used in the event of partial styles on sheets
 * Styles are usually @type { Partial<Style> }, so when they need to be rendered this
 * sheet will fill in missing properties
 */
const styles = styleSheetNew({
  line,
  text,
});

export function isSolidTextBackground(background: TextBackground | undefined): boolean {
  return (background ?? text.background) === TextBackground.SOLID;
}

export function isOutlineTextBackground(background: TextBackground | undefined): boolean {
  return (background ?? text.background) === TextBackground.OUTLINE;
}

export default styles as RequiredStyleSheet<typeof styles>;

// the below functions are works in progress, and for now only do the bare minimum to maintain plan set baselines.
// These need more product input to determine things like fontSize thresholds and line width fallbacks to maintain
// the legibility of plan sets when scaled

export function lineStyleScale<T extends LineStyle>(style: T, scale: number): T {
  if (scale === 1) {
    return style;
  }

  const copy: Mutable<T> = { ...style };

  if (copy.linewidth) {
    copy.linewidth = numMul(copy.linewidth, scale);
  }

  if (copy.dashLength) {
    copy.dashLength = copy.dashLength.map((x) => numMul(x, scale));
  }

  return copy;
}

export function hatchStyleScale<T extends LineStyle>(style: T, scale: number): T {
  if (scale === 1) {
    return style;
  }

  const copy: Mutable<T> = { ...style };

  if (copy.hatchLineSpacing) {
    copy.hatchLineSpacing = numMul(copy.hatchLineSpacing, scale);
  }

  if (copy.hatchLineWidth) {
    copy.hatchLineWidth = numMul(copy.hatchLineWidth, scale);
  }

  return copy;
}

export function textStyleScale<T extends TextStyle>(style: T, scale: number): T {
  if (scale === 1) {
    return style;
  }

  const copy: Mutable<T> = { ...style };

  if (copy.fontSize) {
    copy.fontSize = numMul(copy.fontSize, scale);
  }

  if (copy.strokeWidth) {
    copy.strokeWidth = numMul(copy.strokeWidth, scale);
  }

  return copy;
}
